/**
 * Preset Voice Admin Service
 *
 * Service for managing preset voices (admin-created voice personas):
 * - YouTube audio extraction
 * - Voice creation via XTTS v2
 * - Test voice generation
 * - CRUD operations on nav/data/preset_voices
 *
 * @author Claude AI for TGE.LA
 * @version 1.0.0
 */

import {
  collection,
  doc,
  getDoc,
  getDocs,
  updateDoc,
  deleteDoc,
  Timestamp
} from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import { navDb, functions } from '../firebase/client';

// Voice service URL - update with your Cloud Run URL
const voiceServiceEnv = (import.meta as ImportMeta & { env?: Record<string, string | undefined> }).env || {};
const VOICE_SERVICE_URL =
  voiceServiceEnv.VITE_NAVIGATORX_VOICE_SERVICE_URL ||
  voiceServiceEnv.VITE_VOICE_SERVICE_URL ||
  'https://navigatorx-voice-rxvjkiddqa-uc.a.run.app';

// Firestore collection path
const PRESET_VOICES_PATH = 'nav/data/preset_voices';

// ============================================================================
// TYPE DEFINITIONS
// ============================================================================

export type VoiceCategory = 'PROFESSIONAL' | 'REGIONAL' | 'CHARACTER' | 'LEGENDS' | 'ADMIN_PUBLIC';
export type UnlockType = 'FREE' | 'PRO_ONLY' | 'ACHIEVEMENT';
export type UnlockRequirementType = 'MILES_DRIVEN' | 'WELLS_VISITED' | 'HAZARDS_REPORTED' | 'STREAK_DAYS';

export interface UnlockRequirement {
  type: UnlockRequirementType;
  value: number;
}

export interface PresetVoice {
  id: string;
  name: string;
  description: string;
  category: VoiceCategory;
  voiceEmbeddingPath: string;
  sourceUrl?: string;
  sourceDescription?: string;
  unlockType: UnlockType;
  unlockRequirement?: UnlockRequirement;
  iconEmoji?: string;
  previewAudioUrl?: string;
  isHighQuality: boolean;
  isEnabled: boolean;
  isNew: boolean;
  qualityScore?: number;
  createdAt: Timestamp | Date;
  createdBy: string;
  updatedAt: Timestamp | Date;
}

export interface ExtractYouTubeRequest {
  youtubeUrl: string;
  startTime?: number;
  duration?: number;
}

export interface ExtractYouTubeResponse {
  success: boolean;
  audioPath: string;
  durationSeconds: number;
  signedUrl: string;
}

export interface CreatePresetVoiceRequest {
  name: string;
  description: string;
  category: VoiceCategory;
  audioPath: string;
  sourceUrl?: string;
  sourceDescription?: string;
  unlockType: UnlockType;
  unlockRequirement?: UnlockRequirement;
  iconEmoji?: string;
  adminId: string;
}

export interface CreatePresetVoiceResponse {
  success: boolean;
  voiceId: string;
  embeddingPath: string;
  previewAudioUrl: string;
  qualityScore: number;
}

export interface TestVoiceRequest {
  voiceId: string;
  text: string;
}

export interface TestVoiceResponse {
  success: boolean;
  audioBase64: string;
  durationSeconds: number;
}

// ============================================================================
// API FUNCTIONS
// ============================================================================

/**
 * Extract audio from YouTube video for voice cloning
 */
export async function extractYouTubeAudio(
  request: ExtractYouTubeRequest
): Promise<ExtractYouTubeResponse> {
  const response = await fetch(`${VOICE_SERVICE_URL}/admin/extract-youtube`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      youtube_url: request.youtubeUrl,
      start_time: request.startTime,
      duration: request.duration || 60
    })
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({ detail: 'Unknown error' }));
    throw new Error(error.detail || `HTTP ${response.status}`);
  }

  const data = await response.json();
  return {
    success: data.success,
    audioPath: data.audio_path,
    durationSeconds: data.duration_seconds,
    signedUrl: data.signed_url
  };
}

/**
 * Create a new preset voice from extracted audio
 */
export async function createPresetVoice(
  request: CreatePresetVoiceRequest
): Promise<CreatePresetVoiceResponse> {
  const response = await fetch(`${VOICE_SERVICE_URL}/admin/create-preset-voice`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      name: request.name,
      description: request.description,
      category: request.category,
      audio_path: request.audioPath,
      source_url: request.sourceUrl,
      source_description: request.sourceDescription,
      unlock_type: request.unlockType,
      unlock_requirement: request.unlockRequirement,
      icon_emoji: request.iconEmoji,
      admin_id: request.adminId
    })
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({ detail: 'Unknown error' }));
    throw new Error(error.detail || `HTTP ${response.status}`);
  }

  const data = await response.json();
  return {
    success: data.success,
    voiceId: data.voice_id,
    embeddingPath: data.embedding_path,
    previewAudioUrl: data.preview_audio_url,
    qualityScore: data.quality_score
  };
}

/**
 * Test a preset voice with sample text
 */
export async function testPresetVoice(
  request: TestVoiceRequest
): Promise<TestVoiceResponse> {
  const response = await fetch(`${VOICE_SERVICE_URL}/admin/test-preset-voice`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      voice_id: request.voiceId,
      text: request.text
    })
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({ detail: 'Unknown error' }));
    throw new Error(error.detail || `HTTP ${response.status}`);
  }

  const data = await response.json();
  return {
    success: data.success,
    audioBase64: data.audio_base64,
    durationSeconds: data.duration_seconds
  };
}

// ============================================================================
// FIRESTORE FUNCTIONS
// ============================================================================

/**
 * Get all preset voices from Firestore
 */
export async function getPresetVoices(): Promise<PresetVoice[]> {
  const collectionRef = collection(navDb, 'nav', 'data', 'preset_voices');
  const snapshot = await getDocs(collectionRef);

  const getTimestampValue = (value: unknown): number => {
    if (!value) return 0;
    if (value instanceof Timestamp) return value.toMillis();
    if (value instanceof Date) return value.getTime();
    if (typeof value === 'string' || typeof value === 'number') {
      const parsed = new Date(value).getTime();
      return Number.isFinite(parsed) ? parsed : 0;
    }
    if (typeof value === 'object' && value !== null && 'seconds' in (value as Record<string, unknown>)) {
      const seconds = Number((value as { seconds?: unknown }).seconds);
      return Number.isFinite(seconds) ? seconds * 1000 : 0;
    }
    return 0;
  };

  return snapshot.docs
    .map(doc => ({
      id: doc.id,
      ...doc.data()
    }) as PresetVoice)
    .sort((a, b) => {
      const aTime = getTimestampValue(a.updatedAt) || getTimestampValue(a.createdAt);
      const bTime = getTimestampValue(b.updatedAt) || getTimestampValue(b.createdAt);
      return bTime - aTime;
    });
}

/**
 * Get a single preset voice by ID
 */
export async function getPresetVoice(voiceId: string): Promise<PresetVoice | null> {
  const docRef = doc(navDb, 'nav', 'data', 'preset_voices', voiceId);
  const docSnap = await getDoc(docRef);

  if (!docSnap.exists()) {
    return null;
  }

  return {
    id: docSnap.id,
    ...docSnap.data()
  } as PresetVoice;
}

/**
 * Update a preset voice
 */
export async function updatePresetVoice(
  voiceId: string,
  updates: Partial<PresetVoice>
): Promise<void> {
  const docRef = doc(navDb, 'nav', 'data', 'preset_voices', voiceId);
  await updateDoc(docRef, {
    ...updates,
    updatedAt: Timestamp.now()
  });
}

/**
 * Toggle preset voice enabled/disabled
 */
export async function togglePresetVoiceEnabled(
  voiceId: string,
  enabled: boolean
): Promise<void> {
  await updatePresetVoice(voiceId, { isEnabled: enabled });
}

/**
 * Toggle preset voice "NEW" badge
 */
export async function togglePresetVoiceNew(
  voiceId: string,
  isNew: boolean
): Promise<void> {
  await updatePresetVoice(voiceId, { isNew });
}

/**
 * Delete a preset voice (calls voice service for full cleanup)
 */
export async function deletePresetVoice(
  voiceId: string,
  adminId: string
): Promise<{ success: boolean; message: string }> {
  try {
    const deletePresetVoiceFn = httpsCallable(functions, 'deletePresetVoice');
    const result = await deletePresetVoiceFn({ voiceId, adminId });
    const data = result.data as { success: boolean; message?: string };

    return {
      success: data.success,
      message: data.message || (data.success ? 'Preset voice deleted successfully' : 'Deletion failed')
    };
  } catch (error) {
    try {
      await deleteDoc(doc(navDb, 'nav', 'data', 'preset_voices', voiceId));
      return {
        success: true,
        message: 'Preset voice deleted from Firestore (storage cleanup requires deployed callable)'
      };
    } catch (deleteError: any) {
      throw new Error(deleteError.message || (error instanceof Error ? error.message : 'Failed to delete preset voice'));
    }
  }
}

// ============================================================================
// CONSTANTS
// ============================================================================

export const VOICE_CATEGORIES: { value: VoiceCategory; label: string; description: string }[] = [
  { value: 'PROFESSIONAL', label: 'Professional', description: 'Clear, professional navigation voices' },
  { value: 'REGIONAL', label: 'Regional', description: 'Voices with regional accents and flavor' },
  { value: 'CHARACTER', label: 'Character', description: 'Fun character voices' },
  { value: 'LEGENDS', label: 'Legends', description: 'Unlockable achievement voices' },
  { value: 'ADMIN_PUBLIC', label: 'Admin Public', description: 'Admin-created voices for Pro users' }
];

export const UNLOCK_TYPES: { value: UnlockType; label: string; description: string }[] = [
  { value: 'FREE', label: 'Free', description: 'Available to all users' },
  { value: 'PRO_ONLY', label: 'Pro Only', description: 'Requires Pro subscription' },
  { value: 'ACHIEVEMENT', label: 'Achievement', description: 'Unlock through achievements' }
];

export const UNLOCK_REQUIREMENT_TYPES: { value: UnlockRequirementType; label: string; unit: string }[] = [
  { value: 'MILES_DRIVEN', label: 'Miles Driven', unit: 'miles' },
  { value: 'WELLS_VISITED', label: 'Wells Visited', unit: 'wells' },
  { value: 'HAZARDS_REPORTED', label: 'Hazards Reported', unit: 'hazards' },
  { value: 'STREAK_DAYS', label: 'Day Streak', unit: 'days' }
];

export const SAMPLE_NAVIGATION_PHRASES = [
  "Turn left in 500 feet onto Highway 75.",
  "In 2 miles, take the exit toward Odessa.",
  "You have arrived at your destination.",
  "Continue straight for 15 miles.",
  "Turn right at the next intersection.",
  "Recalculating route. Please continue ahead."
];

// ============================================================================
// DEFAULT EXPORT
// ============================================================================

const presetVoiceAdminService = {
  extractYouTubeAudio,
  createPresetVoice,
  testPresetVoice,
  getPresetVoices,
  getPresetVoice,
  updatePresetVoice,
  togglePresetVoiceEnabled,
  togglePresetVoiceNew,
  deletePresetVoice,
  VOICE_CATEGORIES,
  UNLOCK_TYPES,
  UNLOCK_REQUIREMENT_TYPES,
  SAMPLE_NAVIGATION_PHRASES
};

export default presetVoiceAdminService;
